import {Component, DestroyRef, EventEmitter, OnInit, Output, inject} from '@angular/core';
import {
  BokkzAlgDataService, BokkzClient,
  BokkzSalarisClient,
  BokkzWageDataService,
  ClientEntity, CustomerResponse,
  WerkgeverEntity,
  WerknemerEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute} from '@angular/router';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {dispatchDeletedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {Observable, Subject, distinctUntilChanged, map, switchMap, tap} from 'rxjs';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {ContextMenuItem} from "@snelstart/ui-context-menu";
import {DatePipe} from "@angular/common";
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-wage-list',
  templateUrl: './feature-tax-wage-list.component.html',
  styleUrl: './feature-tax-wage-list.component.scss',
  imports: [
    SharedUiButtonModule,
    SharedUiTableModule,
    SharedUiIconModule,
    SharedUiTooltipModule,
    DatePipe,
    SharedUiAlertModule,
  ],
})
export class FeatureTaxWageListComponent implements OnInit {

  @Output() public details = new EventEmitter<number>();
  @Output() public employeeSettings = new EventEmitter<WerknemerEntity | undefined>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectEmployeeId$ = new Subject<string>();
  public readonly bokkzAlgDataService = inject(BokkzAlgDataService);
  public readonly bokkzAlgClient = inject(BokkzClient);
  public readonly dataService = inject(BokkzWageDataService);
  public readonly bokkzClient = inject(BokkzSalarisClient);
  public readonly toastService = inject(ToastService);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected tableConfig!: TableConfiguration<any, any>;
  public clientId = '';
  public showTable = false;
  public profielNietIngevuld = false;
  public werknemerEntity= {} as WerknemerEntity;
  public year = 0;
  public werkgeverEntity = {} as WerkgeverEntity;
  public werknemerEntityList: WerknemerEntity[] = [];
  public clientEntity = {} as ClientEntity;

  private getWerknemerNaam(entity: WerknemerEntity): string {
    const p = entity?.werknemerGegevens?.persoon;

    const voorletters = (p?.voorletters ?? '').trim();
    const tussenvoegsel = (p?.tussenvoegsel ?? '').trim();
    const achternaam = (p?.achternaam ?? '').trim();

    const fullName = `${voorletters} ${tussenvoegsel} ${achternaam}`.replace(/\s+/g, ' ').trim();
    return fullName || 'deze werknemer';
  }

  ngOnInit(): void {
    this.selectEmployeeId$.pipe(
      distinctUntilChanged(),
      switchMap((id) => this.bokkzClient.getOneEmployee(this.clientId, id)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((werknemerEntity) => {
      this.dataService.updateWerknemer(werknemerEntity);
    });

    this.createTableConfig();
    this.observeContext();
    this.observeState();
    queueMicrotask(() => {
      this.showTable = true;
    });
  }

  private observeContext(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nextClientId = params['clientId'] ?? '';
      if (!nextClientId || nextClientId === this.clientId) {
        return;
      }

      this.clientId = nextClientId;
      this.loadClient();
      this.loadEmployer();
      this.createTableConfig();
    });
  }

  private observeState(): void {
    this.bokkzAlgDataService.client.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.clientEntity = value;
    });

    this.bokkzAlgDataService.selectedYear.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.year = value;
      this.createTableConfig();
    });

    this.dataService.werknemerList.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.werknemerEntityList = value ?? [];
      this.updateProfielNietIngevuld(this.werknemerEntityList);
    });
  }

  private loadClient(): void {
    this.bokkzAlgClient
      .getClient(this.clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.bokkzAlgDataService.updateClient(value);
        this.clientEntity = value;
      });
  }

  private loadEmployer(): void {
    this.bokkzClient
      .getOneEmployer(this.clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((werkgeverEntity) => {
        this.werkgeverEntity = werkgeverEntity;
        this.dataService.updateWerkgever(werkgeverEntity);
      });
  }

  private updateProfielNietIngevuld(werknemerslijst: WerknemerEntity[]): void {
    // Show an alert when at least one employee has no profile linked to the first dienstverband.
      if (werknemerslijst && werknemerslijst.length > 0) {
          this.profielNietIngevuld = (werknemerslijst ?? []).some(w => {
              const profielId = w?.werknemerGegevens?.dienstverbanden?.at(0)?.profielId;
              return profielId === null || profielId === '';
          });
      }
  }

  private createTableConfig(): void {
    const criteria = new SearchCriteria();

    this.tableConfig = TableConfiguration.create({
      identifier: 'id',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: criteria,
      search: () => this.bokkzClient.getAllEmployees(this.clientId).pipe(
        map((value) => {
          this.dataService.updateWerknemerList(value);
          this.werknemerEntityList = value;
          this.updateProfielNietIngevuld(value);
          return new SearchResult<WerknemerEntity>(value, value.length);
        })
      ),
      details: (entity) => {
        if (entity.id != undefined) {
          this.dataService.updateSelectedTab(1);
          this.selectEmployeeId$.next(String(entity.id));
          this.details.next(entity.id);
        }
      },
      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, WerknemerEntity>;
      },
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2026 ? 'Module niet beschikbaar' : $localize`Geen werknemers gevonden`,
        subText:
          this.year < 2026
            ? 'De module salarisadministratie wordt door SnelStart ondersteund vanaf fiscaal jaar 2025.'
            : 'Maak een nieuwe aangifte aan en start direct met invullen.',
        buttonText: this.year < 2026 ? undefined : `Maak nieuwe werknemer aan`,
        buttonConfig:
          this.year < 2026
            ? undefined
            : ButtonConfiguration.create({
              onClick: () => {
                return new Observable((subscriber) => {
                  setTimeout(() => {
                    this.showDialog();
                    subscriber.next(true);
                    subscriber.complete();
                  }, 50);
                });
              },
            }),
      }),
      contextMenu: (entity) => (menu) => {
        if (entity.id === undefined) return;
        const settingsContextMenuItem: ContextMenuItem<CustomerResponse> = {
          icon: 'gear',
          iconType: 'light',
          name: $localize`Instellingen werknemer`,
          onClick: () => {
            this.werknemerEntity = entity;
            this.dataService.updateWerknemer(entity);
            this.showDialog()
          },
        };
        const deleteContextMenuItem: ContextMenuItem<Response> = {
          icon: 'trash',
          iconType: 'light',
          colorType: 'danger',
          name: $localize`Verwijderen werknemer`,
          confirm: {
            type: 'delete',
            title: 'Verwijderen werknemer en dienstverband',
            body:
              'Weet je zeker dat je van ' +
              "'" +
              this.getWerknemerNaam(entity) +
              "'" +
              ' de werknemergegevens en dienstverband definitief wilt verwijderen?',
          },
          onClick: () =>
            this.bokkzClient.deleteEmployee(this.clientId, String(entity.id)).pipe(
              tap((response) => {
                if (response.ok) {
                  this.toastService.showSuccessMessage('Werknemer verwijderd');
                  this.createTableConfig();
                  dispatchDeletedItemEvent({identifier: entity.id!});
                } else {
                  this.toastService.showErrorMessage('Werknemer kon niet verwijderd worden');
                }
              })
            ),
        };

        return menu.createGroup().addItem(settingsContextMenuItem).addItem(deleteContextMenuItem);
      },
    });
  }

  public showDialog() {
    this.employeeSettings.emit(this.werknemerEntity?.id ? this.werknemerEntity : undefined);
  }
}
